import React, { useContext, useEffect } from "react";
import { useParams } from "react-router-dom";
import { AlertCircle, Check, Loader2, Star } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { FeatureFlagContext } from "context/featureFlagContext";
import { authorization } from "module/auth";
import { formatReviewOfferCopy, getReviewOfferCopy } from "./reviewOfferCopy";
import { useReviewOfferState, ReviewOfferAnalyticsEvent, ReviewOfferAnalyticsProps } from "./useReviewOfferState";
import { recordReviewOfferFunnelStage } from "./reviewOfferFunnelApi";
import { ReviewOfferWidgetPopup } from "./ReviewOfferWidgetPopup";
import { G2ReviewModal } from "./G2ReviewModal";
import { DEFAULT_REVIEW_OFFER_PERCENT, ReviewOfferEntitlement } from "./types";
import { DiscountChoice } from "./useDiscountExclusivity";

export type ReviewOfferRowProps = {
  campaignId?: string | number;
  /** unused by the eligibility call (session-resolved, no params) — kept for the widget's `locale` attr once i18n ships */
  countryCode?: string;
  /**
   * "one discount per order" (HUB-454, frontend-spec.md S5/S6). `null` means
   * neither discount is currently selected (normal S1-S4/S7/S8 flow, HUB-453
   * behavior unchanged). `"coupon"` means a manual coupon is the order's
   * current pick — the row renders its greyed "blocked" variant instead of
   * S1/S4 whenever it would otherwise be eligible/applied, and its CTA opens
   * the swap dialog instead of the Thanos widget.
   */
  discountChoice: DiscountChoice;
  /**
   * Blocked-variant CTA when an entitlement is ALREADY active (state S4) —
   * opens the couponToReview confirm dialog. Safe to clear the coupon
   * immediately in this case since the entitlement already exists (no
   * zero-discount window).
   */
  onRequestSwapToReview: () => void;
  /**
   * Blocked-variant CTA when the user is only ELIGIBLE (state S1, no
   * entitlement yet) — there is nothing to swap TO yet, so this starts the
   * pending-swap flow (owned by useDiscountExclusivity: the coupon is only
   * cleared once the entitlement actually lands) instead of opening the
   * confirm dialog. See Greptile #1 on PR #2402.
   */
  onStartPendingReviewSwap: () => void;
  /** true while a swap (dialog-confirmed or pending) is in flight — disables the blocked CTA to avoid double-firing */
  isSwapBusy?: boolean;
  onEntitlementChange?: (entitlement: ReviewOfferEntitlement | null) => void;
};

/**
 * Last row of the Quote card (frontend-spec.md, placement locked to Option A).
 * States S1 (default) / S2 (popup, handled as an overlay sibling) / S3
 * (applying) / S4 (applied) / S7 (error) / S8 (hidden), plus the S5 "blocked
 * by an active coupon" variant (HUB-454). Checks is_review_discount_enabled
 * at its own top-level render — flag off means zero DOM footprint AND the
 * eligibility endpoint is never called.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- countryCode reserved for the widget's `locale` attr once i18n ships
export const ReviewOfferRow = ({
  campaignId,
  countryCode,
  discountChoice,
  onRequestSwapToReview,
  onStartPendingReviewSwap,
  isSwapBusy,
  onEntitlementChange,
}: ReviewOfferRowProps) => {
  const { featureFlags, loading: flagsLoading } = useContext(FeatureFlagContext);
  const posthog = usePostHog();
  const flagEnabled = !flagsLoading && !!featureFlags.isReviewDiscountEnabled;
  const g2Enabled = flagEnabled && !!featureFlags.isReviewOfferG2EmbedEnabled;

  const user = authorization.getUser();
  // The widget mount below requires orgId, so an undefined value silently makes
  // the whole review flow unreachable (the CTA appears to do nothing). The
  // session user is not a reliable source: platform login stores the client as
  // a nested object (`client.id`), other shapes use `clientId`, and signup /
  // REST-login sessions may carry neither. The builder route always carries the
  // client id, so prefer it and keep the user shapes as fallbacks.
  const { clientId: routeClientId } = useParams<{ clientId?: string }>();
  const rawOrgId = routeClientId ?? user?.clientId ?? user?.client?.id;
  const orgId = rawOrgId != null ? String(rawOrgId) : undefined;
  const userId = user?.id != null ? String(user.id) : undefined;

  const onAnalyticsEvent = (event: ReviewOfferAnalyticsEvent, props?: ReviewOfferAnalyticsProps) => {
    posthog?.capture(event, { event_category: "Quote", campaign_id: campaignId, ...props });
    // HUB-477: same event, same call site, additionally persisted server-side
    // so oppizi-api can fire a sequence trigger at each funnel stage (PostHog
    // is not readable by the trigger engine). Fire-and-forget; no UI change.
    // The flag guard mirrors the render guard below — with the flag off this
    // component returns null before any event can fire, but the check is kept
    // explicit so the zero-footprint guarantee does not depend on that.
    if (flagEnabled) recordReviewOfferFunnelStage(event, campaignId);
  };

  // NOTE (HUB-454): eligibility is now fetched purely off the flag, NOT off
  // whether a coupon is applied — the row must still know whether the user
  // is eligible/entitled while a coupon is active, in order to render the S5
  // blocked variant below instead of hiding entirely (HUB-453's old behavior).
  const { state, entitlement, program, discountPercent, openWidget, closeWidget, retry, g2 } = useReviewOfferState({
    flagEnabled,
    g2Enabled,
    campaignId,
    onAnalyticsEvent,
  });

  // HUB-466: copy + percent are resolved by program — an active entitlement
  // (S4) is the authoritative source for both (it's what was actually
  // granted); before one exists (S1/S2), the eligibility response's own
  // program/discount_percent describe what the client would get.
  const copy = getReviewOfferCopy(entitlement?.program ?? program);
  const percent = entitlement?.discount_percent ?? discountPercent ?? DEFAULT_REVIEW_OFFER_PERCENT;

  useEffect(() => {
    onEntitlementChange?.(entitlement);
  }, [entitlement, onEntitlementChange]);

  useEffect(() => {
    if (state === "S1") onAnalyticsEvent("review_offer_viewed");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  if (!flagEnabled || state === "S8") return null;

  // S5 (frontend-spec.md): a manual coupon is the order's current choice —
  // never render the normal S1 (eligible) / S4 (applied) row; render the
  // greyed "one discount per order" variant instead. The CTA still "opens"
  // something rather than doing nothing — "looks disabled but still works"
  // per spec — but WHICH action depends on whether there's an entitlement to
  // swap to yet (Greptile #1 on PR #2402):
  //  - state S4 (entitlement already active): safe to confirm the swap right
  //    away, so the CTA opens the couponToReview confirm dialog.
  //  - state S1 (only eligible, no entitlement yet): there's nothing to swap
  //    TO — clearing the coupon now would leave the order with zero
  //    discounts. Instead the CTA starts the pending-swap flow (which keeps
  //    the coupon until the entitlement actually lands) and opens the review
  //    widget so the user can go earn it.
  if (discountChoice === "coupon" && (state === "S1" || state === "S4")) {
    const onBlockedCtaClick = () => {
      if (isSwapBusy) return;
      if (state === "S4") {
        onRequestSwapToReview();
      } else {
        onStartPendingReviewSwap();
        openWidget();
      }
    };

    return (
      <div
        className="flex items-center justify-between gap-3 border-t border-solid border-border bg-muted px-5 py-[13px]"
        data-cypress-id="reviewOfferRowBlockedByCoupon"
      >
        <div className="flex items-center gap-3">
          <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-muted-foreground/15">
            <Star className="h-4 w-4 text-muted-foreground" />
          </span>
          <div className="flex flex-col">
            <span className="text-[14px] font-semibold leading-5 text-muted-foreground">
              {formatReviewOfferCopy(copy.offer.headline, percent)}
            </span>
            <span className="text-[12px] leading-4 text-muted-foreground">{copy.offer.exclusiveTooltip}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={onBlockedCtaClick}
          disabled={isSwapBusy}
          title={copy.offer.exclusiveTooltip}
          className="h-9 shrink-0 cursor-not-allowed rounded-lg border border-solid border-border bg-background px-4 text-[13.5px] font-semibold text-muted-foreground shadow-[0_1px_2px_0_rgba(2,6,24,0.06)] disabled:opacity-60"
          data-cypress-id="reviewOfferSwapToReviewButton"
        >
          {copy.offer.cta}
        </button>
      </div>
    );
  }

  if (state === "S4") {
    return (
      <div
        className="flex items-center gap-3 border-t border-solid border-border bg-[rgba(0,199,88,0.08)] px-5 py-[13px] dark:bg-[rgba(0,199,88,0.14)]"
        data-cypress-id="reviewOfferRowApplied"
      >
        <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-[rgba(0,199,88,0.16)]">
          <Check className="h-4 w-4 text-[#009244] dark:text-[#4ADE80]" />
        </span>
        <div className="flex flex-col">
          <span className="text-[14px] font-[650] leading-5 text-[#0B7A3B] dark:text-[#4ADE80]">
            {formatReviewOfferCopy(copy.offer.applied.title, percent)}
          </span>
          <span className="text-[12.5px] leading-4 text-muted-foreground">{copy.offer.applied.sub}</span>
        </div>
      </div>
    );
  }

  if (state === "S3") {
    return (
      <div
        className="flex items-center gap-3 border-t border-solid border-border bg-[rgba(63,199,250,0.09)] px-5 py-[13px] dark:bg-[rgba(63,199,250,0.08)]"
        data-cypress-id="reviewOfferRowApplying"
      >
        <Loader2 className="h-[18px] w-[18px] shrink-0 animate-spin text-[#0A7196] dark:text-[#3FC7FA]" />
        <span className="text-[14px] font-semibold leading-5 text-[#0F5C7A] dark:text-[#7DD3FC]">
          {copy.offer.applying}
        </span>
      </div>
    );
  }

  if (state === "S7") {
    return (
      <div
        className="flex items-center justify-between gap-3 border-t border-solid border-border bg-red-50 px-5 py-[13px] dark:bg-red-950/20"
        data-cypress-id="reviewOfferRowError"
      >
        <div className="flex items-center gap-3">
          <AlertCircle className="h-[18px] w-[18px] shrink-0 text-destructive" />
          <span className="text-[14px] font-medium leading-5 text-destructive">{copy.offer.error.title}</span>
        </div>
        <button
          type="button"
          onClick={retry}
          className="h-9 shrink-0 rounded-lg border border-solid border-destructive/40 bg-background px-4 text-[13.5px] font-semibold text-destructive shadow-[0_1px_2px_0_rgba(2,6,24,0.06)] hover:bg-red-50 dark:hover:bg-red-950/30"
          data-cypress-id="reviewOfferRetryButton"
        >
          {copy.offer.error.retry}
        </button>
      </div>
    );
  }

  // S1 (and S2, which renders the row unchanged behind the popup overlay)
  return (
    <>
      <div
        className="flex items-center justify-between gap-3 border-t border-solid border-border bg-[rgba(63,199,250,0.09)] px-5 py-[13px] dark:bg-[rgba(63,199,250,0.08)]"
        data-cypress-id="reviewOfferRow"
      >
        <div className="flex items-center gap-3">
          <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-[rgba(63,199,250,0.18)]">
            <Star className="h-4 w-4 text-[#0A7196] dark:text-[#3FC7FA]" />
          </span>
          <span className="text-[14px] font-semibold leading-5 text-foreground">
            {formatReviewOfferCopy(copy.offer.headline, percent)}
          </span>
        </div>
        <button
          type="button"
          onClick={openWidget}
          className="h-9 shrink-0 rounded-lg border border-solid border-[rgba(10,113,150,0.4)] bg-background px-4 text-[13.5px] font-semibold text-[#0A7196] shadow-[0_1px_2px_0_rgba(2,6,24,0.06)] hover:bg-[#F2FBFE] dark:border-[rgba(63,199,250,0.4)] dark:text-[#3FC7FA] dark:hover:bg-white/5"
          data-cypress-id="reviewOfferCtaButton"
        >
          {copy.offer.cta}
        </button>
      </div>
      {state === "S2" && g2Enabled && g2.formUrl && (
        <G2ReviewModal
          formUrl={g2.formUrl}
          program={program}
          submitting={g2.submitting}
          unconfirmed={g2.unconfirmed}
          onSubmitted={g2.submit}
          onClose={closeWidget}
          onOpened={() => onAnalyticsEvent("review_popup_opened")}
        />
      )}
      {state === "S2" && !g2Enabled && orgId && userId && (
        <ReviewOfferWidgetPopup
          orgId={orgId}
          userId={userId}
          campaignId={campaignId}
          onClose={closeWidget}
          onOpened={() => onAnalyticsEvent("review_popup_opened")}
        />
      )}
    </>
  );
};
